import { toIsoDate, toText } from "../dataImport/parseCell";
import type { ImportRow } from "../dataImport/types";
import {
  GUIDE_FORM_SOURCE,
  VOGELKOP_SOURCE_NAMES,
  VOGELKOP_STAGES,
  WEBSITE_CONTACT_SOURCE,
} from "../../../../supabase/functions/_shared/vogelkop.ts";

export type LeadImportFields = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  background?: string;
  stage?: string;
  sourceName?: string;
  first_seen?: string;
  assignee?: string;
};

const seenOn = (value: string | undefined) => {
  const isoDay = value ? toIsoDate(value) : undefined;
  if (isoDay) return isoDay;
  if (value && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  return undefined;
};

const cell = (row: ImportRow, header: string) => {
  const wanted = header.trim().toLowerCase();
  for (const [key, value] of Object.entries(row)) {
    if (key.trim().toLowerCase() === wanted) return toText(value);
  }
  return undefined;
};

export const splitPersonName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: undefined, last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
};

export const mapLeadStage = (raw: string | undefined) => {
  const text = raw?.trim().toLowerCase();
  if (!text) return undefined;
  if (text === "lead" || text === "new") return "new";
  if (text === "contacted") return "contacted";
  if (text.includes("appointment")) return "appointment-set";
  if (text === "negotiation") return "negotiation";
  if (text === "won" || text === "sold") return "won";
  if (text === "lost") return "lost";
  return VOGELKOP_STAGES.find(
    (stage) =>
      stage.value.toLowerCase() === text || stage.label.toLowerCase() === text,
  )?.value;
};

export const mapLeadSource = (raw: string | undefined) => {
  const text = raw?.trim().toLowerCase();
  if (!text) return undefined;
  if (text.includes("website")) return WEBSITE_CONTACT_SOURCE;
  if (text.includes("guide") || text.includes("before you buy")) {
    return GUIDE_FORM_SOURCE;
  }
  if (text.includes("instagram")) return "Instagram lead ad";
  if (text.includes("facebook")) return "Facebook lead ad";
  if (text.includes("spreadsheet")) return "Spreadsheet import";
  if (text.includes("added by hand")) return "Added by hand";
  if (text === "text message") return "Text message";
  return (
    VOGELKOP_SOURCE_NAMES.find((name) => name.toLowerCase() === text) ??
    raw?.trim()
  );
};

/** Reads either the CRM export headers or a deal-export spreadsheet. */
export const leadImportFields = (row: ImportRow): LeadImportFields => {
  const named = splitPersonName(
    cell(row, "Deal Contact") ?? cell(row, "first_name") ?? "",
  );
  const explicitLast = cell(row, "last_name");
  const message = cell(row, "Message");
  const notes = cell(row, "Notes") ?? cell(row, "background");
  const extras = [
    cell(row, "Deal ID") ? `Deal ID: ${cell(row, "Deal ID")}` : undefined,
    cell(row, "Value") ? `Value: ${cell(row, "Value")}` : undefined,
    cell(row, "Probability to win")
      ? `Probability to win: ${cell(row, "Probability to win")}`
      : undefined,
    cell(row, "Forecasted value")
      ? `Forecasted value: ${cell(row, "Forecasted value")}`
      : undefined,
    cell(row, "Expected Close Date")
      ? `Expected close: ${cell(row, "Expected Close Date")}`
      : undefined,
    cell(row, "Assignee") ? `Assignee: ${cell(row, "Assignee")}` : undefined,
  ].filter((line): line is string => Boolean(line));
  const background = [
    message,
    notes && notes !== message ? notes : undefined,
    extras.length ? extras.join("\n") : undefined,
  ]
    .filter((line): line is string => Boolean(line))
    .join("\n\n");

  return {
    first_name: named.first_name ?? cell(row, "first_name"),
    last_name: explicitLast ?? named.last_name,
    email: cell(row, "Deal Contact Email") ?? cell(row, "email_work"),
    phone: cell(row, "Phone") ?? cell(row, "phone_work"),
    background: background || undefined,
    stage: mapLeadStage(cell(row, "Stage") ?? cell(row, "stage")),
    sourceName: mapLeadSource(cell(row, "Lead source") ?? cell(row, "source")),
    first_seen: seenOn(
      cell(row, "Deal Created Date") ?? cell(row, "first_seen"),
    ),
    assignee: cell(row, "Assignee"),
  };
};
